import { TokenInterceptor } from './tokenInterceptor';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ftflows.org/api';
//export const API_BASE_URL = 'https://localhost:5001/api';

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

export class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
    if (token) {
      localStorage.setItem('auth-token', token);
    } else {
      localStorage.removeItem('auth-token');
    }
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const currentToken = localStorage.getItem('auth-token');
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    return headers;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>('GET', endpoint);
  }

  async post<T, R>(endpoint: string, data: T): Promise<R> {
    return this.makeRequest<R>('POST', endpoint, data);
  }

  async put<T, R>(endpoint: string, data: T): Promise<R> {
    return this.makeRequest<R>('PUT', endpoint, data);
  }

  async delete(endpoint: string): Promise<void> {
    return this.makeRequest<void>('DELETE', endpoint);
  }

  async getBlob(endpoint: string): Promise<{ blob: Blob; fileName?: string }> {
    const headers: HeadersInit = {};
    const currentToken = localStorage.getItem('auth-token');
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        const refreshSuccess = await this.handleUnauthorized();
        if (refreshSuccess) {
          return this.getBlob(endpoint);
        }
      }
      throw await this.handleError(response);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName: string | undefined;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) {
        fileName = match[1].replace(/['"]/g, '').trim();
      }
    }
    return { blob, fileName };
  }

  private async handleError(response: Response): Promise<ApiError> {
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();
      const errorMessage = errorData.error?.message || errorData.message || 'Невідома помилка сервера';
      return {
        message: errorMessage,
        status: response.status,
        errors: errorData.errors,
      };
    } else {
      return {
        message: `HTTP Error: ${response.status} ${response.statusText}`,
        status: response.status,
      };
    }
  }

  private async makeRequest<T>(
    method: string,
    endpoint: string,
    data?: any,
    retryCount: number = 0
  ): Promise<T> {
    const maxRetries = 1;

    try {
      const options: RequestInit = {
        method,
        headers: this.getHeaders(),
      };

      if (data && (method === 'POST' || method === 'PUT')) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, options);

      if (!response.ok) {
        if (response.status === 401 && retryCount < maxRetries) {
          const refreshSuccess = await this.handleUnauthorized();
          if (refreshSuccess) {
            return this.makeRequest<T>(method, endpoint, data, retryCount + 1);
          }
        }
        throw await this.handleError(response);
      }

      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as any;
      }

      return response.json();
    } catch (error) {
      // Для sync запитів (version та sync) - не retry тут, polling сам повторить
      // Retry тільки для 401 (refresh token)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Помилка підключення до сервера');
      }
      throw error;
    }
  }

  private async handleUnauthorized(): Promise<boolean> {
    return await TokenInterceptor.handleUnauthorized();
  }
}

export const apiClient = new ApiClient();
